import React from 'react';
import {
  Heart,
  Clock,
  Shield,
  CheckCircle,
  Activity,
  Calendar,
  Droplet,
  Camera,
  Award,
  Smile,
  Sparkles,
  ArrowRight
} from 'lucide-react';

const UrgencyLevelCard = ({ nivelUrgencia, edad }) => {
  if (!nivelUrgencia) return null;

  // Sample data based on screenshots
  const exampleData = {
    color: 'verde',
    mensaje: '¡Excelentes noticias! Tu lomito está en un estado de salud envidiable, ¡eres un humano ejemplar!',
    tiempoRecomendado: 'Mantén sus visitas al veterinario cada 6 meses para chequeos preventivos. La próxima cita puede ser en agosto del 2025 para mantener este excelente estado de salud',
    razones: [
      '¡Su peso de 7-8 kg es perfecto! Se nota una estructura corporal bien proporcionada',
      'Su pelaje blanco está limpio y bien mantenido, señal de buenos cuidados diarios',
      '¡Qué alegría y energía muestra en las fotos! Su postura y mirada reflejan a un perrito feliz y saludable',
      'No se observan problemas de piel ni señales de malestar - ¡excelente cuidado!'
    ],
    accionesInmediatas: [
      'Continúa con su rutina actual de alimentación: está funcionando de maravilla',
      'Sigue con esos buenos hábitos de cepillado que mantienen su pelaje tan bonito',
      'Mantén su agua siempre fresquita y limpia como hasta ahora',
      'Guarda estas fotos para comparar su evolución en su próxima revisión',
      'No olvides mantener su calendario de desparasitación cada 3 meses y sus vacunas al día'
    ]
  };

  // Use props or example data
  const data = {
    color: nivelUrgencia.color || exampleData.color,
    mensaje: nivelUrgencia.mensaje || exampleData.mensaje,
    tiempoRecomendado: nivelUrgencia.tiempoRecomendado || exampleData.tiempoRecomendado,
    razones: nivelUrgencia.razones || exampleData.razones,
    accionesInmediatas: nivelUrgencia.accionesInmediatas || exampleData.accionesInmediatas
  };

  // Color and icon configurations
  const configMap = {
    verde: {
      mainBorder: 'border-l-[6px] border-l-green-500',
      cardBg: 'bg-green-50/70',
      headerIcon: Heart,
      headerIconBg: 'bg-green-100',
      headerIconColor: 'text-green-600',
      statusColor: 'text-green-700',
      badgeColor: 'bg-green-100 text-green-700 border-green-200',
      dividerColor: 'bg-green-200/50',
      dotColor: 'bg-green-400',
      sectionBg: 'bg-white/80',
      buttonBg: 'bg-green-100 hover:bg-green-200',
      buttonTextColor: 'text-green-700',
      findingsIcon: Shield,
      findingsIconBg: 'bg-green-100',
      findingsIconColor: 'text-green-600',
      recommendIcon: CheckCircle,
      recommendIconBg: 'bg-green-100',
      recommendIconColor: 'text-green-600',
      patternColor: 'text-green-100',
      highlightText: 'text-green-700'
    },
    amarillo: {
      mainBorder: 'border-l-[6px] border-l-yellow-500',
      cardBg: 'bg-yellow-50/70',
      headerIcon: Clock,
      headerIconBg: 'bg-yellow-100',
      headerIconColor: 'text-yellow-600',
      statusColor: 'text-yellow-700',
      badgeColor: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      dividerColor: 'bg-yellow-200/50',
      dotColor: 'bg-yellow-400',
      sectionBg: 'bg-white/80',
      buttonBg: 'bg-yellow-100 hover:bg-yellow-200',
      buttonTextColor: 'text-yellow-700',
      findingsIcon: Activity,
      findingsIconBg: 'bg-yellow-100',
      findingsIconColor: 'text-yellow-600',
      recommendIcon: CheckCircle,
      recommendIconBg: 'bg-yellow-100',
      recommendIconColor: 'text-yellow-600',
      patternColor: 'text-yellow-100',
      highlightText: 'text-yellow-700'
    },
    naranja: {
      mainBorder: 'border-l-[6px] border-l-orange-500',
      cardBg: 'bg-orange-50/70',
      headerIcon: Clock,
      headerIconBg: 'bg-orange-100',
      headerIconColor: 'text-orange-600',
      statusColor: 'text-orange-700',
      badgeColor: 'bg-orange-100 text-orange-700 border-orange-200',
      dividerColor: 'bg-orange-200/50',
      dotColor: 'bg-orange-400',
      sectionBg: 'bg-white/80',
      buttonBg: 'bg-orange-100 hover:bg-orange-200',
      buttonTextColor: 'text-orange-700',
      findingsIcon: Activity,
      findingsIconBg: 'bg-orange-100',
      findingsIconColor: 'text-orange-600',
      recommendIcon: CheckCircle,
      recommendIconBg: 'bg-green-100',
      recommendIconColor: 'text-green-600',
      patternColor: 'text-orange-100',
      highlightText: 'text-orange-700'
    },
    rojo: {
      mainBorder: 'border-l-[6px] border-l-red-500',
      cardBg: 'bg-red-50/70',
      headerIcon: Activity,
      headerIconBg: 'bg-red-100',
      headerIconColor: 'text-red-600',
      statusColor: 'text-red-700',
      badgeColor: 'bg-red-100 text-red-700 border-red-200',
      dividerColor: 'bg-red-200/50',
      dotColor: 'bg-red-400',
      sectionBg: 'bg-white/80',
      buttonBg: 'bg-red-100 hover:bg-red-200',
      buttonTextColor: 'text-red-700',
      findingsIcon: Activity,
      findingsIconBg: 'bg-red-100',
      findingsIconColor: 'text-red-600',
      recommendIcon: CheckCircle,
      recommendIconBg: 'bg-green-100',
      recommendIconColor: 'text-green-600',
      patternColor: 'text-red-100',
      highlightText: 'text-red-700'
    }
  };

  const config = configMap[data.color] || configMap.verde;
  const HeaderIcon = config.headerIcon;
  const FindingsIcon = config.findingsIcon;
  const RecommendIcon = config.recommendIcon;

  // Function to add icons to specific recommendations
  const getRecommendationIcon = (text) => {
    if (text.toLowerCase().includes('alimentación')) return <Award className="h-4 w-4 text-amber-500" />;
    if (text.toLowerCase().includes('cepillado') || text.toLowerCase().includes('pelaje')) return <Smile className="h-4 w-4 text-purple-500" />;
    if (text.toLowerCase().includes('agua')) return <Droplet className="h-4 w-4 text-blue-500" />;
    if (text.toLowerCase().includes('fotos')) return <Camera className="h-4 w-4 text-indigo-500" />;
    if (text.toLowerCase().includes('vacunas') || text.toLowerCase().includes('desparasitación')) return <Calendar className="h-4 w-4 text-red-500" />;
    return <CheckCircle className="h-4 w-4 text-green-500" />;
  };

  return (
    <div className="space-y-4">
      {/* Age Information if provided */}
      {edad && (
        <div className="bg-blue-50 rounded-xl mb-4 overflow-hidden shadow-sm">
          <div className="flex items-center p-4">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center shadow-sm mr-3">
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-blue-800">Edad aproximada</h3>
              <p className="text-lg text-gray-700">{edad}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Health Card */}
      <div className={`rounded-xl overflow-hidden shadow-md ${config.mainBorder} ${config.cardBg} relative`}>
        {/* Decorative Pattern */}
        <div className="absolute top-0 right-0 w-32 h-32 overflow-hidden opacity-10">
          <div className="rotate-45 transform origin-top-right">
            {Array(5).fill(0).map((_, i) => (
              <div key={i} className={`w-16 h-16 ${config.patternColor}`}>
                <Sparkles className="w-full h-full" />
              </div>
            ))}
          </div>
        </div>

        {/* Header Section */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start gap-4">
            <div className={`rounded-full h-12 w-12 ${config.headerIconBg} flex items-center justify-center flex-shrink-0 shadow-sm border border-white/80`}>
              <HeaderIcon className={`h-6 w-6 ${config.headerIconColor}`} />
            </div>
            
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-800 mb-1">Estado de Salud</h2>
              <p className={`text-lg font-medium ${config.statusColor}`}>{data.mensaje}</p>
            </div>
          </div>
          
          {/* Visit Recommendation Banner */}
          <div className={`mt-4 p-3 rounded-lg ${config.badgeColor} border shadow-sm flex items-start gap-2.5 text-sm`}>
            <Clock className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <p>{data.tiempoRecomendado}</p>
          </div>
        </div>

        {/* Content Divider */}
        <div className={`h-1 w-full ${config.dividerColor}`}></div>

        {/* Two Columns Content */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Findings Section */}
          <div className={`${config.sectionBg} rounded-xl p-5 shadow-sm border border-gray-100`}>
            <div className="flex items-center gap-2 mb-4">
              <div className={`h-8 w-8 rounded-full ${config.findingsIconBg} border border-white shadow-sm flex items-center justify-center`}>
                <FindingsIcon className={`h-4 w-4 ${config.findingsIconColor}`} />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Lo que encontramos:</h3>
            </div>
            
            <ul className="space-y-3.5">
              {data.razones.map((razon, idx) => (
                <li key={idx} className="flex items-start gap-3 group">
                  <span className={`h-4 w-4 rounded-full ${config.dotColor} flex-shrink-0 mt-1 group-hover:scale-110 transition-transform`}></span>
                  <span className="text-gray-700">{razon}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Recommendations Section */}
          <div className="bg-white/80 rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-8 w-8 rounded-full bg-green-100 border border-white shadow-sm flex items-center justify-center">
                <RecommendIcon className="h-4 w-4 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Recomendaciones:</h3>
            </div>
            
            <ul className="space-y-3.5">
              {data.accionesInmediatas.map((accion, idx) => (
                <li key={idx} className="flex items-start gap-3 group">
                  <div className="bg-white h-5 w-5 rounded-full shadow-sm border border-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform">
                    {getRecommendationIcon(accion)}
                  </div>
                  <span className="text-gray-700">{accion}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UrgencyLevelCard;